import * as back from '~/components/layouts/Navigation/BreadcrumbContainer/functional/backLinks';
import {
  crudBreadcrumb,
  detailBreadcrumb,
  listBreadcrumb,
  nestedCrudBreadcrumb,
  nestedListBreadcrumb,
} from '~/components/layouts/Navigation/BreadcrumbContainer/functional/generators';
import * as match from '~/components/layouts/Navigation/BreadcrumbContainer/functional/matchers';
import type { RouteRule, BreadcrumbContext, BreadcrumbItem } from '~/components/layouts/Navigation/BreadcrumbContainer/functional/types';

const PRIORITY = {
  SECTION_BASE: 285,
  LIST_PAGE: 340,
  ADD_FORM: 350,
  EDIT_FORM: 360,
  DETAIL_PAGE: 355,
  TAX_DOCUMENTS: 340,
  GENERATE_TAX_DOCS: 345,
  WORK_ORDERS: 300,
  WORK_ORDER_ADD: 315,
  WORK_ORDER_EDIT: 310,
  WORK_ORDER_DETAIL: 305,
  MAINTENANCE_REQUEST: 320,
  MAINTENANCE_REQUEST_ADD: 335,
  MAINTENANCE_REQUEST_EDIT: 330,
  MAINTENANCE_REQUEST_DETAIL: 325,
  AI_RUNBOOKS: 290,
  AI_RUNBOOK_DETAIL: 295,
} as const;

const vendorDetailBreadcrumb = (action?: string, actionPath?: string) => (context: BreadcrumbContext) => {
  const vendor = context.currentVendor?.();
  const vendorName = vendor?.name || context.t('Loading');
  const vendorId = vendor?.id;

  const breadcrumbs: BreadcrumbItem[] = [
    { label: context.t('Maintenance') },
    { label: context.t('Vendors'), link: '/maintenance/vendors' },
    { label: vendorName, link: `/maintenance/vendors/${vendorId}` },
  ];

  if (action) {
    breadcrumbs.push({ label: context.t(action), ...(actionPath && { link: actionPath }) });
  }

  return breadcrumbs;
};

export const maintenanceRules: RouteRule[] = [
  {
    match: match.and(match.startsWith('/maintenance/vendors'), match.includes('/edit')),
    generate: vendorDetailBreadcrumb('Edit'),
    backLink: back.goBack,
    priority: PRIORITY.EDIT_FORM,
  },
  {
    match: match.and(match.startsWith('/maintenance/vendors'), match.hasId()),
    generate: vendorDetailBreadcrumb(),
    backLink: back.goBack,
    priority: PRIORITY.DETAIL_PAGE,
  },
  {
    match: match.and(match.startsWith('/maintenance/vendors'), match.includes('/add')),
    generate: crudBreadcrumb('Maintenance', '/maintenance', 'Vendors', '/maintenance/vendors', 'Add vendor'),
    backLink: back.goBack,
    priority: PRIORITY.ADD_FORM,
  },
  {
    match: match.and(match.startsWith('/maintenance/vendors'), match.includes('tax-documents'), match.includes('generate')),
    generate: nestedCrudBreadcrumb(
      'Maintenance',
      '/maintenance',
      'Vendors',
      '/maintenance/vendors',
      'Tax documents',
      '/maintenance/vendors/tax-documents',
      'Generate tax documents',
      '/maintenance/vendors/tax-documents/generate'
    ),
    backLink: back.goBack,
    priority: PRIORITY.GENERATE_TAX_DOCS,
  },
  {
    match: match.and(match.startsWith('/maintenance/vendors'), match.includes('tax-documents')),
    generate: nestedListBreadcrumb(
      'Maintenance',
      '/maintenance',
      'Vendors',
      '/maintenance/vendors',
      'Tax documents',
      '/maintenance/vendors/tax-documents'
    ),
    backLink: back.goBack,
    priority: PRIORITY.TAX_DOCUMENTS,
  },
  {
    match: match.and(match.startsWith('/maintenance'), match.includes('/vendors')),
    generate: listBreadcrumb('Maintenance', '/maintenance', 'Vendors', '/maintenance/vendors'),
    backLink: back.homeLink,
    priority: PRIORITY.LIST_PAGE,
  },
  {
    match: match.and(match.includes('/maintenance/maintenance-request'), match.includes('/add')),
    generate: crudBreadcrumb(
      'Maintenance',
      '/maintenance',
      'Maintenance requests',
      '/maintenance/maintenance-request',
      'Add maintenance request'
    ),
    backLink: back.goBack,
    priority: PRIORITY.MAINTENANCE_REQUEST_ADD,
  },
  {
    match: match.and(match.includes('/maintenance/maintenance-request'), match.includes('/edit')),
    generate: crudBreadcrumb(
      'Maintenance',
      '/maintenance',
      'Maintenance requests',
      '/maintenance/maintenance-request',
      'Edit maintenance request'
    ),
    backLink: back.goBack,
    priority: PRIORITY.MAINTENANCE_REQUEST_EDIT,
  },
  {
    match: match.and(match.includes('/maintenance/maintenance-request'), match.hasId()),
    generate: detailBreadcrumb(
      'Maintenance',
      '/maintenance',
      'Maintenance requests',
      '/maintenance/maintenance-request',
      'Maintenance request details'
    ),
    backLink: back.goBack,
    priority: PRIORITY.MAINTENANCE_REQUEST_DETAIL,
  },
  {
    match: match.includes('/maintenance/maintenance-request'),
    generate: listBreadcrumb('Maintenance', '/maintenance', 'Maintenance requests', '/maintenance/maintenance-request'),
    backLink: back.homeLink,
    priority: PRIORITY.MAINTENANCE_REQUEST,
  },
  {
    match: match.and(match.includes('/work-orders'), match.includes('/add')),
    generate: crudBreadcrumb('Maintenance', '/maintenance', 'Work orders', '/maintenance/work-orders', 'Add work order'),
    backLink: back.goBack,
    priority: PRIORITY.WORK_ORDER_ADD,
  },
  {
    match: match.and(match.includes('/work-orders'), match.includes('/edit')),
    generate: crudBreadcrumb('Maintenance', '/maintenance', 'Work orders', '/maintenance/work-orders', 'Edit work order'),
    backLink: back.goBack,
    priority: PRIORITY.WORK_ORDER_EDIT,
  },
  {
    match: match.and(match.includes('/work-orders'), match.hasId()),
    generate: detailBreadcrumb('Maintenance', '/maintenance', 'Work orders', '/maintenance/work-orders', 'Work order details'),
    backLink: back.goBack,
    priority: PRIORITY.WORK_ORDER_DETAIL,
  },
  {
    match: match.includes('/work-orders'),
    generate: listBreadcrumb('Maintenance', '/maintenance', 'Work orders', '/maintenance/work-orders'),
    backLink: back.homeLink,
    priority: PRIORITY.WORK_ORDERS,
  },
  {
    match: match.and(match.includes('/ai-runbooks'), match.hasId()),
    generate: detailBreadcrumb('Maintenance', '/maintenance', 'AI runbooks', '/maintenance/ai-runbooks', 'AI runbook detail'),
    backLink: back.goBack,
    priority: PRIORITY.AI_RUNBOOK_DETAIL,
  },
  {
    match: match.includes('/ai-runbooks'),
    generate: listBreadcrumb('Maintenance', '/maintenance', 'AI runbooks', '/maintenance/ai-runbooks'),
    backLink: back.homeLink,
    priority: PRIORITY.AI_RUNBOOKS,
  },
  {
    match: match.startsWith('/maintenance'),
    generate: (context) => [{ label: context.t('Maintenance') }],
    backLink: back.homeLink,
    priority: PRIORITY.SECTION_BASE,
  },
];
